// NotificationBell — колокольчик в топбаре: бейдж непрочитанного (поллинг),
// дропдаун последних 5, тост при появлении новых. Клик по элементу = mark-read
// + переход в карточку клиента (если payload содержит clientId).
//
// НЕ ВСЕ ВИДЫ ТОСТЯТСЯ. Записи и отмены записи копятся сотнями: за неделю 388
// «клиент записался» и 286 «запись отменена» — почти сотня всплывающих окон в
// день на человека, и тосты не гаснут сами (висят до закрытия). Администратор
// работал сквозь стену попапов. По решению владельца 03.09.2026 эти два вида
// БОЛЬШЕ НЕ ВСПЛЫВАЮТ, но пишутся как прежде: в колокольчике и в разделе
// «Уведомления» они на месте, ничего не потеряно.
import { Bell, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { formatDate, formatTime } from '@/lib/format';
import { cn } from '@/lib/utils';

import type { EmployeeNotification } from '../api/notifications.api';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationsList,
  useNotificationsSummary,
} from '../hooks/useNotifications';
import { describeNotification } from '../lib/notification-display';

/**
 * Виды, которые пишутся в журнал, но НЕ всплывают тостом (решение владельца
 * 03.09.2026). Здесь только шумные потоковые события; всё, что требует
 * вмешательства человека — заявки на привязку, смену телефона, задачи —
 * тостится по-прежнему.
 */
const SILENT_KINDS = new Set(['enrollment', 'enrollment_cancelled']);

export function NotificationBell(): JSX.Element {
  const navigate = useNavigate();
  const summary = useNotificationsSummary();
  const list = useNotificationsList({ limit: 5 });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const [open, setOpen] = useState(false);
  // Стек информативных тостов о новых уведомлениях. НЕ гаснут сами — висят, пока
  // не закроешь крестиком или кликом (запрос владельца). Стек прокручиваемый.
  const [toasts, setToasts] = useState<EmployeeNotification[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  // id уже показанных элементов — чтобы поллинг не тостил одно и то же повторно.
  const seenIds = useRef<Set<string> | null>(null);

  const unread = summary.data?.unread ?? 0;

  const listData = list.data;
  // Новые непрочитанные элементы между поллингами → тосты. Первый прогон — только
  // засев seenIds (без тостов при первой загрузке страницы).
  useEffect(() => {
    if (listData === undefined) return;
    if (seenIds.current === null) {
      seenIds.current = new Set(listData.map((n) => n.id));
      return;
    }
    const fresh = listData.filter(
      (n) => !seenIds.current!.has(n.id) && !n.read_at && !SILENT_KINDS.has(n.kind),
    );
    for (const n of listData) seenIds.current.add(n.id);
    if (fresh.length > 0) {
      // Не обрезаем стек: тосты висят до ручного закрытия (seenIds не даёт задвоить).
      setToasts((cur) => [...fresh, ...cur]);
    }
  }, [listData]);

  /** Закрыть тост, не отмечая прочитанным и без перехода (крестик). */
  function dismissToast(id: string): void {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }

  function navigateTo(href: string | null, clientId: string | null): void {
    if (href) navigate(href);
    else if (clientId) navigate(`/clients?client=${clientId}`);
    else navigate('/notifications');
  }

  function handleToastClick(n: EmployeeNotification): void {
    const d = describeNotification(n);
    markRead.mutate(n.id);
    setToasts((cur) => cur.filter((t) => t.id !== n.id));
    navigateTo(d.href, d.clientId);
  }

  // Клик вне дропдауна — закрыть.
  useEffect(() => {
    if (!open) return undefined;
    function onDown(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const items = list.data ?? [];

  function handleItemClick(n: EmployeeNotification): void {
    const d = describeNotification(n);
    markRead.mutate(n.id);
    setOpen(false);
    navigateTo(d.href, d.clientId);
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-9 w-9 items-center justify-center rounded-base text-ink-black transition-colors hover:bg-divider-gray/40 focus:outline-none focus:ring-2 focus:ring-primary"
        aria-label="Уведомления"
        title="Уведомления"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-soft-orange px-1 text-[10px] font-semibold leading-none text-canvas-white">
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-card border border-divider-gray bg-canvas-white shadow-lg">
          <div className="flex items-center justify-between border-b border-divider-gray px-3 py-2">
            <span className="text-sm font-medium">Уведомления</span>
            {unread > 0 ? (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                className="text-xs font-medium text-primary hover:underline"
              >
                Прочитать все
              </button>
            ) : null}
          </div>

          <ul className="max-h-80 divide-y divide-divider-gray overflow-y-auto">
            {items.length === 0 ? (
              <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                Нет уведомлений
              </li>
            ) : (
              items.map((n) => {
                const d = describeNotification(n);
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => handleItemClick(n)}
                      className={cn(
                        'flex w-full flex-col items-start gap-0.5 px-3 py-2.5 text-left hover:bg-muted',
                        n.read_at ? 'opacity-60' : '',
                      )}
                    >
                      <span className="flex w-full items-center gap-2">
                        {!n.read_at ? (
                          <span className="h-2 w-2 shrink-0 rounded-full bg-soft-orange" />
                        ) : null}
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">
                          {d.title}
                        </span>
                      </span>
                      {d.subtitle ? (
                        <span className="line-clamp-2 text-xs text-muted-foreground">
                          {d.subtitle}
                        </span>
                      ) : null}
                      <span className="text-[11px] text-muted-foreground">
                        {formatDate(n.created_at)} {formatTime(n.created_at)}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/notifications');
            }}
            className="block w-full border-t border-divider-gray px-3 py-2 text-center text-sm font-medium text-primary hover:bg-muted"
          >
            Все уведомления
          </button>
        </div>
      ) : null}

      {/* Стек тостов (правый верхний угол). Не гаснут сами: клик по телу → переход +
          прочитано; крестик → просто убрать тост. Прокрутка при накоплении. */}
      {toasts.length > 0 ? (
        <div className="fixed right-4 top-16 z-[60] flex max-h-[80vh] w-80 flex-col gap-2 overflow-y-auto">
          {toasts.map((n) => {
            const d = describeNotification(n);
            return (
              <div
                key={n.id}
                className="relative rounded-card border border-divider-gray bg-canvas-white shadow-lg transition-colors hover:bg-muted"
              >
                <button
                  type="button"
                  onClick={() => handleToastClick(n)}
                  className="flex w-full flex-col items-start gap-0.5 px-4 py-2 pr-8 text-left"
                >
                  <span className="text-sm font-medium text-ink-black">{d.title}</span>
                  {d.subtitle ? (
                    <span className="line-clamp-2 text-xs text-muted-foreground">{d.subtitle}</span>
                  ) : null}
                </button>
                <button
                  type="button"
                  onClick={() => dismissToast(n.id)}
                  aria-label="Закрыть"
                  title="Закрыть"
                  className="absolute right-1.5 top-1.5 rounded-full p-1 text-muted-foreground hover:bg-divider-gray/40 hover:text-ink-black"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
